import React from "react";
import { Box, Typography, IconButton, Paper } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckIcon from "@mui/icons-material/Check";
import RemoveIcon from "@mui/icons-material/Remove";
import RadioButtonCheckedIcon from "@mui/icons-material/RadioButtonChecked";

const ApprovalHistory = ({ request, onBack }) => {
  const historyItems = request?.history || [
    {
      step: "Request Submitted",
      date: "01 Oct 2026 10:15 AM",
      by: request?.name || "Amila Bandara",
      note: "Initial request submitted",
      status: "completed",
    },
    {
      step: "Manager Approval",
      date: "01 Oct 2026 02:30 PM",
      by: "Nadee Perera",
      note: "Approved",
      status: "completed",
    },
    {
      step: "Director Approval",
      date: "Pending",
      by: "Director",
      note: "Waiting for director approval",
      status: "pending",
    },
    {
      step: "Completed",
      date: "-",
      by: "-",
      note: "Request will be completed after final approval",
      status: "upcoming",
    },
  ];

  const renderStatusIcon = (status) => {
    if (status === "completed") {
      return (
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: "50%",
            backgroundColor: "#22C55E",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#ffffff",
            boxShadow: "0 2px 6px rgba(34, 197, 94, 0.3)",
          }}
        >
          <CheckIcon sx={{ fontSize: 18, stroke: "#ffffff", strokeWidth: 1.5 }} />
        </Box>
      );
    }
    if (status === "pending") {
      return (
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: "50%",
            backgroundColor: "#EFF6FF",
            border: "2.5px solid #0052CC",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#0052CC",
            boxShadow: "0 2px 6px rgba(0, 82, 204, 0.2)",
          }}
        >
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              backgroundColor: "#0052CC",
            }}
          />
        </Box>
      );
    }
    if (status === "rejected") {
      return (
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: "50%",
            backgroundColor: "#EF4444",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#ffffff",
          }}
        >
          <RemoveIcon sx={{ fontSize: 18 }} />
        </Box>
      );
    }
    // upcoming
    return (
      <Box
        sx={{
          width: 28,
          height: 28,
          borderRadius: "50%",
          backgroundColor: "#94A3B8",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#ffffff",
        }}
      >
        <RemoveIcon sx={{ fontSize: 18 }} />
      </Box>
    );
  };

  const getLineColor = (status) => {
    if (status === "completed") return "#22C55E";
    if (status === "pending") return "#0052CC";
    return "#E2E8F0";
  };

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "540px",
        margin: "0 auto",
        minHeight: "100vh",
        backgroundColor: "#F8FAFC",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Blue Header matching app style */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          background: "linear-gradient(180deg, #0056C6 0%, #004AAD 100%)",
          color: "#ffffff",
          px: 2,
          pt: 1.5,
          pb: 2.5,
          borderBottomLeftRadius: 20,
          borderBottomRightRadius: 20,
          boxShadow: "0 4px 16px rgba(0, 74, 173, 0.2)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", position: "relative" }}>
          <IconButton
            onClick={onBack}
            sx={{
              color: "#ffffff",
              p: 0.8,
              mr: 1,
              "&:hover": { backgroundColor: "rgba(255,255,255,0.12)" },
            }}
          >
            <ArrowBackIcon sx={{ fontSize: 24 }} />
          </IconButton>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              fontSize: "1.15rem",
              letterSpacing: "-0.01em",
              flex: 1,
              textAlign: "center",
              pr: 4, // offset back button
            }}
          >
            Approval History
          </Typography>
        </Box>
      </Box>

      {/* Stepper Content */}
      <Box sx={{ p: { xs: 2.5, sm: 3 } }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2.5, sm: 3 },
            borderRadius: "20px",
            backgroundColor: "#ffffff",
            border: "1px solid #E2E8F0",
            boxShadow: "0 2px 10px rgba(0,0,0,0.02)",
          }}
        >
          {historyItems.map((item, index) => {
            const isLast = index === historyItems.length - 1;
            return (
              <Box key={index} sx={{ display: "flex", position: "relative" }}>
                {/* Left Column: Icon + Line */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    mr: 2.5,
                  }}
                >
                  {renderStatusIcon(item.status)}
                  {!isLast && (
                    <Box
                      sx={{
                        width: 2.5,
                        flex: 1,
                        my: 0.8,
                        minHeight: 52,
                        backgroundColor: getLineColor(item.status),
                        borderRadius: 1,
                      }}
                    />
                  )}
                </Box>

                {/* Right Column: Step Info */}
                <Box sx={{ pb: isLast ? 0 : 3.5, flex: 1 }}>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: "1rem",
                      color: item.status === "upcoming" ? "#64748B" : "#1E293B",
                      lineHeight: 1.2,
                      mb: 0.5,
                    }}
                  >
                    {item.step}
                  </Typography>

                  {/* Timestamp / Pending indicator */}
                  <Typography
                    sx={{
                      fontSize: "0.85rem",
                      fontWeight: item.date === "Pending" ? 600 : 500,
                      color: item.date === "Pending" ? "#0052CC" : "#64748B",
                      mb: 0.4,
                    }}
                  >
                    {item.date}
                  </Typography>

                  {/* Actor */}
                  {item.by && item.by !== "-" && (
                    <Typography
                      sx={{
                        fontSize: "0.85rem",
                        color: "#334155",
                        fontWeight: 600,
                        mb: 0.3,
                      }}
                    >
                      By {item.by}
                    </Typography>
                  )}

                  {/* Notes */}
                  {item.note && (
                    <Typography
                      sx={{
                        fontSize: "0.82rem",
                        color: "#94A3B8",
                      }}
                    >
                      {item.note}
                    </Typography>
                  )}
                </Box>
              </Box>
            );
          })}
        </Paper>
      </Box>
    </Box>
  );
};

export default ApprovalHistory;
